import React, { useState, useEffect } from 'react';
import { ArrowLeft, ChevronDown, ListOrdered } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ApiPlayground from '../components/ApiPlayground';
import ContainerPagination from '../components/ContainerPagination';
import DocsBreadcrumb from '../components/DocsBreadcrumb';
import KeyboardShortcutsOverlay from '../components/KeyboardShortcutsOverlay';
import BackToTop from '../components/BackToTop';

const bgImage = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/69qn1rnj_ABSTRACT%20DESIGN.png';

const TOC = [
  { id: 'api-architecture', label: 'API Architecture', children: [
    { id: 'notification-methods', label: 'Notification Methods' },
  ]},
  { id: 'api-request-fields', label: 'API Request Fields', children: [
    { id: 'sample-request', label: 'Sample Request' },
    { id: 'request-reference', label: 'Request Reference' },
  ]},
  { id: 'udf-list', label: 'UDF List' },
  { id: 'api-responses', label: 'API Responses', children: [
    { id: 'sample-response', label: 'Sample XML Response' },
  ]},
  { id: 'diagram', label: 'Sale, 3D Secure Confirm & Complete Pre-Auth Diagram' },
  { id: 'fund-authorisation-routes', label: 'Fund Authorisation Routes' },
  { id: 'integration-process-overview', label: 'Integration Process Overview' },
];

// Flatten TOC for scroll tracking
const flatIds = TOC.flatMap((i) => [i.id, ...(i.children?.map((c) => c.id) || [])]);

const CSHARP_SAMPLE = `HttpWebRequest httpWReq = (HttpWebRequest)WebRequest.Create("https://staging.ecomm365.com/acqapi/service.ashx"); //Create webrequest while setting the API URL to eComm staging environment
UTF8Encoding encoding = new UTF8Encoding(); //Represents a UTF-8 encoding of Unicode characters.

string pData = "username=SomeName"; //API Username
pData += "&password=SomePassword"; //API Password
pData += "&messageId=" + System.Guid.NewGuid().ToString();  //A new GUID is required for every new API Call
pData += "&ApiSignature=SomeAPISignature"; //API Signature

pData += "&Data=" + WebUtility.UrlEncode(@"<R>...</R>"); //Data fields required for request

byte[] data = encoding.GetBytes(pData); //Getting Bytes for data

httpWReq.Method = "POST"; //HTTP Method - POST
httpWReq.ContentType = "application/x-www-form-urlencoded"; //Setting the correct Content Type
httpWReq.ContentLength = data.Length; //Getting Content Length

//Create POST data and convert it to a byte array.
byte[] byteArray = Encoding.UTF8.GetBytes(pData);

// Get the request stream.
Stream dataStream = httpWReq.GetRequestStream();

// Write the data to the request stream.
dataStream.Write(byteArray, 0, byteArray.Length);

// Close the Stream object.
dataStream.Close();

HttpWebResponse response = (HttpWebResponse)httpWReq.GetResponse(); //Assign response to \`response\` variable
string responseString = new StreamReader(response.GetResponseStream()).ReadToEnd(); //Obtain response from post

/* Parse XML from response */
XmlDocument xDoc = new XmlDocument();
xDoc.LoadXml(responseString);
/* END */

/* Obtain information from response fields */
XmlNodeList ResponseR1 = xDoc.GetElementsByTagName("R1");
XmlNodeList ResponseR2 = xDoc.GetElementsByTagName("R2");
//... etc

//Or loop through the elements
/* END */

//More code...`;

const UDF_SAMPLE = `<!-- UDF Demonstration -->
<R>
    <R1>...</R1> 
    ...
    <udf>
        <I>testdata1</I>
        <I>testdata2</I>
    </udf>
</R>`;

const RESPONSE_SAMPLE = `<?xml version="1.0"?>
<R>
    <R1>0000</R1>
    <R2>Successful</R2>
    ...
</R>`;

const CodeBlock = ({ code, lang = 'csharp', testid }) => (
  <div className="relative bg-[#1e1e1e] rounded-lg overflow-hidden border border-gray-700 my-4" data-testid={testid}>
    <div className="flex items-center justify-between px-4 py-2 bg-[#2d2d2d] border-b border-gray-700">
      <span className="text-xs font-mono text-gray-400 uppercase tracking-wide">{lang}</span>
      <button
        onClick={() => navigator.clipboard?.writeText(code)}
        className="text-xs text-gray-400 hover:text-white transition-colors duration-150 font-medium"
        data-testid={`${testid}-copy`}
      >
        Copy
      </button>
    </div>
    <pre className="overflow-x-auto p-4 text-sm leading-relaxed">
      <code className={`language-${lang} text-gray-100 font-mono whitespace-pre`}>{code}</code>
    </pre>
  </div>
);

const ApiOverview = () => {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState(flatIds[0]);
  const [mobileTocOpen, setMobileTocOpen] = useState(false);

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const offset = 140; // header + breathing room
      let current = flatIds[0];
      for (const id of flatIds) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top - offset <= 0) {
          current = id;
        }
      }
      setActiveId(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    setMobileTocOpen(false);
  };

  return (
    <div className="min-h-screen flex flex-col scroll-smooth" data-testid="api-overview-page">
      <Header />
      <div className="h-20" />

      <div className="relative flex-1">
        <div
          className="fixed inset-0 top-20 bg-cover bg-center"
          style={{ backgroundImage: `url(${bgImage})` }}
        />

        <div className="relative z-10 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
          <DocsBreadcrumb section="api-reference" currentId="overview" />
          <button
            onClick={() => navigate('/technical-documentation/api-reference')}
            className="flex items-center gap-2 text-[#1F4E79] hover:text-[#163a5f] font-medium mb-8 transition-colors duration-200"
            data-testid="api-overview-back-button"
          >
            <ArrowLeft size={18} />
            Back to API Reference
          </button>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* ============ MAIN CONTENT ============ */}
            <main className="flex-1 min-w-0 lg:order-1">
              {/* Mobile collapsed TOC pill */}
              <div className="lg:hidden mb-6 border border-gray-200 rounded-xl overflow-hidden bg-white/90 backdrop-blur-sm">
                <button
                  type="button"
                  onClick={() => setMobileTocOpen((o) => !o)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 text-left"
                  aria-expanded={mobileTocOpen}
                  data-testid="mobile-toc-toggle"
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-[#0a1929]">
                    <ListOrdered size={16} className="text-[#ef5b25]" />
                    On this page
                    <span className="text-gray-400 font-normal">({flatIds.length})</span>
                  </span>
                  <ChevronDown
                    size={18}
                    className={`text-gray-500 transition-transform ${mobileTocOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {mobileTocOpen && (
                  <nav className="bg-white max-h-[60vh] overflow-y-auto py-2" data-testid="mobile-toc-panel">
                    {TOC.map((item) => (
                      <div key={item.id}>
                        <a
                          href={`#${item.id}`}
                          onClick={(e) => handleNavClick(e, item.id)}
                          className={`block px-4 py-2 text-sm transition-colors duration-150 border-l-2 ${
                            activeId === item.id
                              ? 'border-[#1F4E79] bg-blue-50 text-[#1F4E79] font-semibold'
                              : 'border-transparent text-gray-700 hover:bg-gray-50 hover:text-[#1F4E79]'
                          }`}
                          data-testid={`m-toc-link-${item.id}`}
                        >
                          {item.label}
                        </a>
                        {item.children && (
                          <div className="ml-3">
                            {item.children.map((child) => (
                              <a
                                key={child.id}
                                href={`#${child.id}`}
                                onClick={(e) => handleNavClick(e, child.id)}
                                className={`block px-4 py-1.5 text-xs transition-colors duration-150 border-l-2 ${
                                  activeId === child.id
                                    ? 'border-[#1F4E79] bg-blue-50/60 text-[#1F4E79] font-semibold'
                                    : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-[#1F4E79]'
                                }`}
                                data-testid={`m-toc-link-${child.id}`}
                              >
                                {child.label}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </nav>
                )}
              </div>

              {/* Title */}
              <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-12 mb-8">
                <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3" data-testid="api-overview-title">
                  API Overview
                </h1>
              </div>

              {/* API Architecture */}
              <section id="api-architecture" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">API Architecture</h2>
                <div className="space-y-4 text-gray-700 text-base leading-relaxed">
                  <p>
                    Approved merchants will receive Staging API credentials from eCOMM which is required when making a request to the API, here the merchant can start sending requests to our service using our API while sending test data.
                  </p>
                  <p>
                    Using our staging platform allows merchants to easily integrate to eCOMM in a safe environment while not effecting anything live. Once staging is complete and the merchant is satisfied with the results, eCOMM will provide the merchant with Live API credentials where they can begin live tests using real live data.
                  </p>
                  <p className="font-semibold">Please ensure the below has been obtained before starting:</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li><strong>eCOMM API Endpoint</strong> — The URL where the POST requests are sent (e.g. <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">https://staging.ecomm365.com/acqapi/service.ashx</code> for staging)</li>
                    <li>Username</li>
                    <li>Password</li>
                    <li>Vendor ID</li>
                  </ul>
                </div>

                {/* Information / Notification Methods */}
                <div id="notification-methods" className="scroll-mt-28 bg-blue-50/90 rounded-lg p-6 sm:p-8 mt-6 border border-blue-200">
                  <h3 className="text-xl font-bold text-[#1F4E79] mb-4">Information — Notification Methods</h3>
                  <div className="space-y-4 text-gray-700 text-base leading-relaxed">
                    <p>
                      For all HTTP requests the content type must be <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">application/x-www-form-urlencoded</code> so that in the URL, all spaces are converted to '+' symbols and special characters are converted to ASCII HEX values.
                    </p>
                    <p>
                      In addition, the merchant will have to supply eCOMM with the IP Address of the server that hosts the application making calls to the eCOMM API. This is to allow eCOMM to 'whitelist' the provided IP, providing clearance to use the web service.
                    </p>
                    <p>
                      The merchant must also supply eCOMM with the desired Notification URL & Notification Method. Notification Methods are: Notification, Server & Both. Notification method is required so the merchant receives the correct data when making the appropriate request:
                    </p>
                    <ol className="list-decimal pl-6 space-y-3">
                      <li>
                        <strong>Notification:</strong> Once a request is made and has passed validations, the merchant will receive a confirmation notification to the Notification URL (e.g. 0000 - Successful). The merchant will also receive a basic XML document back from the HTTP POST response (e.g. 0008 - Confirmation Received).
                      </li>
                      <li>
                        <strong>Server:</strong> Once a request is made and has passed validations, the merchant will receive a full XML document back from the HTTP POST response (e.g. 0044 - Successful Transaction). There is no confirmation notification being sent in this case.
                      </li>
                      <li>
                        <strong>Both:</strong> Once a request is made and has passed validations, the merchant will receive a full XML document back from the HTTP POST response (e.g. 0044 - Successful Transaction). The merchant will also receive a confirmation notification to the Notification URL (e.g. 0000 - Successful).
                      </li>
                    </ol>
                    <p>
                      Please note that the results are based on what Notification Method the merchant chooses, each section will explain the expected outcomes.
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      See the <Link to="/technical-documentation/notifications" className="text-[#1F4E79] underline hover:text-[#163a5f]">notifications</Link> guide for more details
                    </p>
                  </div>
                </div>

                {/* Key Concepts */}
                <div className="mt-8 space-y-4 text-gray-700 text-base leading-relaxed">
                  <p>
                    The tables that follow outline the form fields expected by the API. Form data and all requests submitted by the merchant site must be <a href="http://en.wikipedia.org/wiki/Percent-encoding" target="_blank" rel="noopener noreferrer" className="text-[#1F4E79] underline hover:text-[#163a5f]">URL Encoded</a>.
                  </p>
                  <p>
                    The HTTP Method is POST, all form actions will be POSTED from the merchants website to the API supplied by eCOMM. The supplied Username and Password API credentials must be used to successfully perform a request.
                  </p>
                  <p>
                    The <strong>MessageID</strong> is a global unique identifier (<a href="http://en.wikipedia.org/wiki/Globally_unique_identifier" target="_blank" rel="noopener noreferrer" className="text-[#1F4E79] underline hover:text-[#163a5f]">GUID</a>) — a 128 bit hexadecimal number that ensures each request has a unique reference.
                  </p>
                  <p>
                    The <strong>APISignature</strong> identifies the type of call/request that is being made to the API. It is literally a one word description of the request type. For example, 'register' for the Sale call and 'refund' for refunding a payment. It is essential that the appropriate signature word is included in each request.
                  </p>
                  <p>
                    The <strong>DATA</strong> field is where the form details are sent in XML format. This XML document varies depending on the function of the API being executed. All the inputs from the customer facing form must be sent in an XML document in the DATA field, this XML document must match our listed validations which can be found under each API section.
                  </p>
                  <p>A Sample XML document is included for each request type in this documentation for the merchant's reference.</p>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
                    <p className="text-sm font-mono break-all">
                      <strong>Query Format:</strong> ?Username=SomeName&Password=SomePassword&MessageID=30dd879c-ee2f-11db-8314-0800200c9a66&APISignature=SomeAPISignature&Data=&lt;R&gt;...&lt;/R&gt;
                    </p>
                  </div>
                  <p>This is required in order to access our services correctly without error.</p>
                  <p>
                    In each API section and under data required, the type of value and maximum character length expected are indicated in the <strong>Field Definition</strong> column. The merchant must ensure the form does not return values that exceed these limits in order to avoid error responses from the API. Please ensure all specified fields are validated accordingly to prevent errors.
                  </p>
                </div>

                {/* Field Definition Table */}
                <div className="mt-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Field Definition — Explanation</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse" data-testid="field-definition-table">
                      <thead>
                        <tr className="bg-[#1F4E79] text-white">
                          <th className="text-left p-3 font-semibold">Data Type</th>
                          <th className="text-left p-3 font-semibold">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-gray-200">
                          <td className="p-3 font-mono font-semibold">N(int)</td>
                          <td className="p-3">A Numeric sequence of up to <code className="bg-gray-100 px-1 rounded">int</code> digits. <strong>Accepts:</strong> '0-9'.</td>
                        </tr>
                        <tr className="border-b border-gray-200 bg-gray-50/50">
                          <td className="p-3 font-mono font-semibold">A(alpha)</td>
                          <td className="p-3">An alpha only string of up to <code className="bg-gray-100 px-1 rounded">int</code> characters. <strong>Accepts:</strong> 'A–Z' & 'a-z'.</td>
                        </tr>
                        <tr className="border-b border-gray-200">
                          <td className="p-3 font-mono font-semibold">AN(alphanumeric)</td>
                          <td className="p-3">An alphanumeric string of up to <code className="bg-gray-100 px-1 rounded">int</code> characters. <strong>Accepts:</strong> 'a-z', 'A-Z' & '0–9'.</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>

              {/* API Request Fields */}
              <section id="api-request-fields" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">API Request Fields</h2>
                <p className="text-gray-700 text-base leading-relaxed mb-4">Below is the required format to access our services from the API:</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-[#1F4E79] text-white">
                        <th className="text-left p-3 font-semibold">Requirement</th>
                        <th className="text-left p-3 font-semibold">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-200"><td className="p-3 font-semibold">Content-Type</td><td className="p-3 font-mono text-sm">application/x-www-form-urlencoded</td></tr>
                      <tr className="border-b border-gray-200 bg-gray-50/50"><td className="p-3 font-semibold">HTTP Method</td><td className="p-3">Post</td></tr>
                      <tr className="border-b border-gray-200"><td className="p-3 font-semibold">Username</td><td className="p-3">Supplied by eCOMM</td></tr>
                      <tr className="border-b border-gray-200 bg-gray-50/50"><td className="p-3 font-semibold">Password</td><td className="p-3">Supplied by eCOMM</td></tr>
                      <tr className="border-b border-gray-200"><td className="p-3 font-semibold">APISignature</td><td className="p-3">Varies depending on API to be invoked</td></tr>
                      <tr className="border-b border-gray-200 bg-gray-50/50"><td className="p-3 font-semibold">MessageID</td><td className="p-3">A Unique GUID to identify the API call<br /><span className="font-mono text-xs">(e.g. 30dd879c-ee2f-11db-8314-0800200c9a66)</span></td></tr>
                      <tr className="border-b border-gray-200"><td className="p-3 font-semibold">Data</td><td className="p-3">Varies depending on API to be invoked</td></tr>
                    </tbody>
                  </table>
                </div>

                {/* Sample Request (C# only) */}
                <div id="sample-request" className="scroll-mt-28 mt-8">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <h3 className="text-xl font-bold text-gray-900">Sample Request</h3>
                    <div className="flex flex-wrap gap-2">
                      <a
                        href="/assets/ecomm-api.postman_collection.json"
                        download="ecomm-api.postman_collection.json"
                        className="inline-flex items-center gap-2 px-3 py-2 bg-[#ef5b25] hover:bg-[#d14f1f] text-white text-xs font-semibold rounded-md shadow-sm transition-colors duration-150"
                        data-testid="download-postman-btn"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <path d="M13.52 10.2l-2.9 2.9-.72-.73c-.14-.15-.38-.15-.52 0-.15.14-.15.38 0 .52l.98.99c.08.07.18.11.27.11.1 0 .19-.04.26-.11l3.16-3.16c.14-.15.14-.38 0-.52-.15-.15-.38-.15-.53 0zM12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0zm0 22C6.48 22 2 17.52 2 12S6.48 2 12 2s10 4.48 10 10-4.48 10-10 10z"/>
                        </svg>
                        Download Postman Collection
                      </a>
                      <a
                        href="/assets/ecomm-api-openapi.yaml"
                        download="ecomm-api-openapi.yaml"
                        className="inline-flex items-center gap-2 px-3 py-2 bg-[#85ea2d] hover:bg-[#6ec723] text-gray-900 text-xs font-semibold rounded-md shadow-sm transition-colors duration-150"
                        data-testid="download-openapi-btn"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                        </svg>
                        Download OpenAPI Spec
                      </a>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mb-3">
                    Import the Postman Collection to test the API in seconds, or use the OpenAPI 3.0 spec to auto-generate SDKs and docs in your preferred language.
                  </p>
                  <CodeBlock code={CSHARP_SAMPLE} lang="csharp" testid="sample-request-code" />
                </div>

                {/* Request Reference */}
                <div id="request-reference" className="scroll-mt-28 mt-10">
                  <ApiPlayground
                    apiSignature="register"
                    title="Request Reference"
                    description="Reference XML request — adapt for your integration."
                    defaultData={`<R>\n    <R1>C</R1>\n    <R2>${(typeof crypto !== 'undefined' && crypto.randomUUID?.()) || 'YOUR-TX-CODE'}</R2>\n    <R3>10000</R3>\n    <R45>YOUR_VENDOR_ID</R45>\n</R>`}
                  />
                </div>
              </section>

              {/* UDF List */}
              <section id="udf-list" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">UDF List</h2>
                <div className="space-y-4 text-gray-700 text-base leading-relaxed">
                  <p>
                    Merchants can provide a UDF list that can contain up to two elements if the merchant wishes to include additional information back in their HTTP POST Response. The UDF list is made up of the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">&lt;udf&gt;</code> parent tag ending with <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">&lt;/udf&gt;</code>. The UDF children is defined using <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">&lt;I&gt;...&lt;/I&gt;</code>.
                  </p>
                  <p>
                    Using the correct format will successfully return the merchants UDF list without error. The merchants UDF list will be returned in the transaction result response. The UDF list must be included within the <strong>DATA</strong> field when making the request and must also be included within the &lt;R&gt; tags. Below, please find the Sample UDF List.
                  </p>
                </div>
                <CodeBlock code={UDF_SAMPLE} lang="xml" testid="udf-sample-code" />
              </section>

              {/* API Responses */}
              <section id="api-responses" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">API Responses</h2>
                <p className="text-gray-700 text-base leading-relaxed mb-4">The API will always return a well formed XML document with the following parameters:</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-[#1F4E79] text-white">
                        <th className="text-left p-3 font-semibold">XML Field Name</th>
                        <th className="text-left p-3 font-semibold">XML Field Data</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-200"><td className="p-3 font-mono font-semibold">R1</td><td className="p-3">Please see the Appendix for a list of available <Link to="/technical-documentation/appendix/error-codes" className="text-[#1F4E79] underline hover:text-[#163a5f]">error codes</Link>.</td></tr>
                      <tr className="border-b border-gray-200 bg-gray-50/50"><td className="p-3 font-mono font-semibold">R2</td><td className="p-3">Textual description of error</td></tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-gray-600 text-sm mt-3">Other data may also be provided depending on the API invoked. Additional response fields will be documented here.</p>

                <div id="sample-response" className="scroll-mt-28 mt-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Sample XML Response</h3>
                  <CodeBlock code={RESPONSE_SAMPLE} lang="xml" testid="response-sample-code" />
                </div>
              </section>

              {/* Diagram */}
              <section id="diagram" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Sale, 3D Secure Confirm & Complete Pre-Auth Diagram</h2>
                <div className="flex justify-center mb-6">
                  <img
                    src="https://dev.ecomm365.com/ecommerce/assets/images/Pages/Overview/SaleConfirmClearDiagram.png"
                    alt="Sale, 3D Secure Confirm & Complete Pre-Auth Diagram"
                    className="max-w-full rounded-lg border border-gray-200"
                    data-testid="api-overview-diagram"
                  />
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
                  <p className="text-gray-800 text-sm font-semibold mb-2">Please Note The Following:</p>
                  <ul className="text-gray-700 text-sm space-y-2 list-disc pl-5">
                    <li>Steps 8 and 9 are only done when the transaction uses authorisation followed by a clearing message at a later point.</li>
                    <li>Steps 3, 4, 5 & 6 are only done if the card is 3D secure.</li>
                    <li>
                      Step 7 only occurs when the notification method is set to <code className="bg-white px-1 rounded">notification</code> or <code className="bg-white px-1 rounded">both</code>, this is set on the eComm side and the merchant must supply what method they wish to use, check out <a href="#notification-methods" onClick={(e) => handleNavClick(e, 'notification-methods')} className="text-[#1F4E79] underline hover:text-[#163a5f]">Notification Methods</a> for more information.
                    </li>
                  </ul>
                </div>
              </section>

              {/* Fund Authorisation Routes */}
              <section id="fund-authorisation-routes" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Fund Authorisation Routes</h2>
                <p className="text-gray-700 text-base leading-relaxed mb-6">
                  eCOMM offers two routes for Authorising funds. Consult with a project manager to determine which route is to be implemented for your business.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                    <h4 className="text-lg font-bold text-[#1F4E79] mb-3">Route 1</h4>
                    <p className="text-gray-700 text-sm mb-3">Makes authorisation and clearing at one go.</p>
                    <ol className="list-decimal pl-5 text-gray-700 text-sm space-y-2">
                      <li>Send a Sale (1) request with C in R1.</li>
                      <li>
                        Send a 3D Secure Confirm (5) request. <span className="text-[#c32222]">(If the card is 3D Secure)</span>
                        <ul className="list-disc pl-5 mt-1 text-gray-600 text-xs italic">
                          <li>Confirm request is not always needed in case of frictionless transaction.</li>
                        </ul>
                      </li>
                    </ol>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                    <h4 className="text-lg font-bold text-[#1F4E79] mb-3">Route 2</h4>
                    <p className="text-gray-700 text-sm mb-3">Splits up authorisation and clearing.</p>
                    <ol className="list-decimal pl-5 text-gray-700 text-sm space-y-2">
                      <li>Send a Sale (1) request with CA in R1.</li>
                      <li>
                        Send a 3D Secure Confirm (5) request. <span className="text-[#c32222]">(If the card is 3D Secure)</span>
                        <ul className="list-disc pl-5 mt-1 text-gray-600 text-xs italic">
                          <li>Confirm request is not always needed in case of frictionless transaction.</li>
                        </ul>
                      </li>
                      <li>Send a Complete Pre-Auth (8) request.</li>
                    </ol>
                  </div>
                </div>
              </section>

              {/* Integration Process Overview */}
              <section id="integration-process-overview" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Integration Process Overview</h2>
                <div className="space-y-4 text-gray-700 text-base leading-relaxed mb-6">
                  <p>
                    The <a href="#diagram" onClick={(e) => handleNavClick(e, 'diagram')} className="text-[#1F4E79] underline hover:text-[#163a5f]">Sale, 3D Secure Confirm & Complete Pre-Auth</a> diagram above shows the process flow for integrating directly with the eCOMM API. This diagram illustrates the sequence of events required to integrate the payment process into a Merchant Site.
                  </p>
                  <p>
                    These pages provide key information for communicating with the API, while using the correct syntax, to successfully complete a transaction. This page gives an overview of the process flow for integrating directly and aims to be a good starting reference point.
                  </p>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 mb-6">
                  <p className="text-gray-800 text-sm font-semibold mb-3">Please Note The Following:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-gray-700">
                    <p><strong>Step 1:</strong> Send Sale Request With Customer Information</p>
                    <p><strong>Step 2:</strong> Receive Sale Response</p>
                    <p><strong>Step 3:</strong> *3D Secure Authentication Information</p>
                    <p><strong>Step 4:</strong> *Receive PARes Payload (3D Secure)</p>
                    <p><strong>Step 5:</strong> *Send 3D Secure Confirm</p>
                    <p><strong>Step 6:</strong> *Receive 3D Secure Confirm Response</p>
                    <p><strong>Step 7:</strong> Receive Confirmation to Notification URL</p>
                    <p><strong>Step 8:</strong> *Send Complete Pre-Auth</p>
                    <p><strong>Step 9:</strong> *Receive Complete Pre-Auth Response</p>
                  </div>
                  <div className="mt-4 text-gray-600 text-xs space-y-1">
                    <p><strong>Steps 3 - 6</strong>, only occurs if the card provided is 3D Secure.</p>
                    <p><strong>Steps 8 - 9</strong>, only occurs if the transaction is a Pre-Auth.</p>
                  </div>
                </div>

                {/* DX API / DDC Pre-flow */}
                <div className="bg-blue-50/80 border border-blue-200 rounded-lg p-5 mb-6">
                  <p className="text-[#1F4E79] text-sm font-semibold mb-3">Device Data Collection (DDC) — Pre-Sale Flow:</p>
                  <ol className="list-decimal pl-5 text-sm text-gray-700 space-y-2">
                    <li><strong>Step 1:</strong> DX API is called with the card details.</li>
                    <li><strong>Step 2:</strong> DX API reference is used to call the DDC.</li>
                    <li><strong>Step 3:</strong> DDC request is made — the first 6 digits of the BIN returned, along with the <code className="bg-white px-1 rounded">dfReference</code>, are used to call the DDC which results in a <code className="bg-white px-1 rounded">paResPayload</code>.</li>
                  </ol>
                  <div className="mt-4 bg-white rounded border border-gray-200 p-3">
                    <p className="text-xs font-semibold text-gray-700 mb-2">Example DX API Request Parameters:</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono text-gray-700">
                      <span><strong>Amount:</strong> 35000</span>
                      <span><strong>BIN:</strong> 446302</span>
                      <span className="sm:col-span-2 break-all"><strong>TransactionCode:</strong> e05e7b42-e36d-427e-9f76-e4261a5b71e9</span>
                      <span><strong>VendorId:</strong> 20001703</span>
                    </div>
                  </div>
                </div>

                {/* Detailed Step Explanations */}
                <h3 className="text-xl font-bold text-gray-900 mb-4 mt-2">Step-by-Step Details</h3>
                <div className="space-y-6 text-gray-700 text-base leading-relaxed">
                  <div>
                    <p className="font-bold text-[#1F4E79] mb-1">Step 1 — Send Sale Request</p>
                    <p>The details entered by a potential customer in the payment page form on the merchant site is collected and sent to our service via the 'Sale' API call. The customer's payment details are assembled in XML format and this data is sent to the API by using the HTTP POST method. All requests to the API must be executed using the POST method while ensuring the URL sent is encoded.</p>
                  </div>
                  <div>
                    <p className="font-bold text-[#1F4E79] mb-1">Step 2 — Receive Sale Response</p>
                    <p className="mb-2">The Sale API will respond with a fully formatted XML document, back to the merchant site which will include an error code and description. The response may also include other data depending if the provided card is 3D Secure or not.</p>
                    <p className="mb-2">If the entered card <strong>is not 3D Secure</strong> and the request is successful, the response may include 0008 as the error code for Confirmation Received, or 0044 for Successful Transaction depending on the Notification Method.</p>
                    <p>If the entered card <strong>is 3D Secure</strong> and the sale request is successful, the &lt;R1&gt; field will contain 0000. The following fields will also be available:</p>
                    <ul className="list-disc pl-6 mt-2 space-y-1 text-sm">
                      <li><strong>ACSURL</strong> (R3 field) — Used to redirect the customer to the 3D Secure service.</li>
                      <li><strong>PAReq</strong> (R4 field) — Contains the encoded authentication request, automatically submitted to the 3D Secure service.</li>
                      <li><strong>PARes</strong> (R5 field) — The reply received from the Issuing Bank after authentication.</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-bold text-[#1F4E79] mb-1">Step 3 — 3D Secure Authentication</p>
                    <p>We use the information in the Sale Response (step 2) to construct a HTML form with data that will be POSTED to the authentication site. The customer provides an extra piece of information — a short password via 'Verified by VISA' or 'MasterCard SecureCode' depending on the card type.</p>
                  </div>
                  <div>
                    <p className="font-bold text-[#1F4E79] mb-1">Step 4 — Receive PARes Payload</p>
                    <p>The payment authentication response (PARes, included in R5 field) is sent back to the merchant site at the Term URL page.</p>
                  </div>
                  <div>
                    <p className="font-bold text-[#1F4E79] mb-1">Step 5 — Send 3D Secure Confirm</p>
                    <p>The PARes value returned from the authenticator is passed to the 'Confirm' API call. The PARes is an alphanumeric string averaging over 3000 characters. Included in this POST are the transaction code from the initial Sale request and the merchant's Vendor ID (VID).</p>
                  </div>
                  <div>
                    <p className="font-bold text-[#1F4E79] mb-1">Step 6 — Receive 3D Secure Confirm Response</p>
                    <p>The XML response is received at the Term URL page including an error code and description. If successful, the response may include 0008 for Confirmation Received or 0044 for Successful Transaction. Any other error code represents an error state.</p>
                  </div>
                  <div>
                    <p className="font-bold text-[#1F4E79] mb-1">Step 7 — Receive Confirmation Notification</p>
                    <p>If the merchant's setup is to send notifications, the results of the completed transaction are sent to the notification URL. The merchant should set up this notification page to read and parse the XML document received from the API. This data includes the results of AVS/CV2 checks, 3D Secure checks and confirmation types.</p>
                  </div>
                  <div>
                    <p className="font-bold text-[#1F4E79] mb-1">Step 8 — Send Complete Pre-Auth</p>
                    <p>If the transaction is a Pre-Auth and the merchant wants to complete it, they can do so by supplying the Transaction Code of the pre-auth transaction and the total amount they wish to complete.</p>
                  </div>
                  <div>
                    <p className="font-bold text-[#1F4E79] mb-1">Step 9 — Receive Complete Pre-Auth Response</p>
                    <p>The merchant will receive the response from the request and if successful, the funds will be processed as normal.</p>
                  </div>
                </div>
              </section>

              <ContainerPagination currentId="overview" />
            </main>

            {/* ============ STICKY SIDEBAR (Table of Contents) ============ */}
            <aside className="hidden lg:block lg:w-64 lg:flex-shrink-0 lg:order-2" data-testid="api-overview-sidebar">
              <div className="lg:sticky lg:top-28">
                <div className="bg-white/85 backdrop-blur-sm rounded-lg shadow-md border border-gray-200 overflow-hidden">
                  <div className="px-4 py-3 bg-[#1F4E79] text-white">
                    <p className="text-sm font-semibold tracking-wide">On This Page</p>
                  </div>
                  <nav className="py-2 max-h-[calc(100vh-10rem)] overflow-y-auto">
                    {TOC.map((item) => (
                      <div key={item.id}>
                        <a
                          href={`#${item.id}`}
                          onClick={(e) => handleNavClick(e, item.id)}
                          className={`block px-4 py-2 text-sm transition-colors duration-150 border-l-2 ${
                            activeId === item.id
                              ? 'border-[#1F4E79] bg-blue-50 text-[#1F4E79] font-semibold'
                              : 'border-transparent text-gray-700 hover:bg-gray-50 hover:text-[#1F4E79]'
                          }`}
                          data-testid={`toc-link-${item.id}`}
                        >
                          {item.label}
                        </a>
                        {item.children && (
                          <div className="ml-3">
                            {item.children.map((child) => (
                              <a
                                key={child.id}
                                href={`#${child.id}`}
                                onClick={(e) => handleNavClick(e, child.id)}
                                className={`block px-4 py-1.5 text-xs transition-colors duration-150 border-l-2 ${
                                  activeId === child.id
                                    ? 'border-[#1F4E79] bg-blue-50/60 text-[#1F4E79] font-semibold'
                                    : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-[#1F4E79]'
                                }`}
                                data-testid={`toc-link-${child.id}`}
                              >
                                {child.label}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </nav>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>

      <div className="relative z-10">
        <Footer />
      </div>
      <KeyboardShortcutsOverlay />
      <BackToTop />
    </div>
  );
};

export default ApiOverview;
